import type { LovCxpiDTO, SalarieDTO } from '@/features/api/extranet/dto/Demande/CXPI'
import type { SalarieRechercheDTO } from '@/features/api/extranet/dto/Demande/shared'
import { useEventBus } from '@/features/application/app-events'
import { ConstantesCIPI } from '@/features/domaine/types/opco/Demande/CXPI'
import {
  buildDemandeActions,
  type DemandeStore,
  NOTIF_SAISIE_SALARIE_MODIFIÉE,
} from '@/features/interface/demande/shared'
import { deepEqual, getAge, isDate, isNullOrUndefined } from '@/features/shared/tools'
import { storeToRefs } from 'pinia'
import { computed, type InjectionKey, watch } from 'vue'

type DemandeCXPIActions = ReturnType<typeof useDemandeCXPIActions>
export const CXPI_ACTIONS: InjectionKey<DemandeCXPIActions> = Symbol('cxpi-actions')

export function useDemandeCXPIActions(store: DemandeStore<'MODEL_CXPI', LovCxpiDTO>) {
  const { model, readonly } = storeToRefs(store)
  const eventBus = useEventBus()

  const actionsDemande = buildDemandeActions({
    store: store as any,
    typeDocumentTemplate: computed(() =>
      model.value?.typeDemande == 'Formation'
        ? model.value?.contrat?.cdiInterimaire !== true
          ? 'ContratCxpi'
          : 'MissionCxpi'
        : null,
    ),
  })

  // Todo: Vérifier si utile
  const alertPlus26AnsLabel = computed(() => {
    if (isDate(model.value?.salarie?.dateNaissance) && isDate(model.value?.contrat?.dateConclusion)) {
      const age = getAge(new Date(model.value.salarie.dateNaissance), new Date(model.value.contrat.dateConclusion))
      // Si +26 ans
      if (
        age >= 26 &&
        isNullOrUndefined(model.value?.salarie?.typeMinimuSocial) === true
        //&& model.value?.salarie?.inscritPoleEmploi !== true
      ) {
        return 'Si le salarié a plus de 25 ans à la date de conclusion du contrat il doit faire partie du public éligible au sens de la règlementation.'
      }
    }
    return undefined
  })

  // Utilisé par la recherche du salarié
  function créerSalariéVide(): SalarieDTO {
    return {
      source: 'Portail',
      adresse: {
        codePostal: null,
        commune: null,
        complement: null,
        mentionsSpeciales: null,
        voie: null,
      },
      beneficieReconnaissanceTravailleurHandicape: null,
      communeNaissance: null,
      csp: null,
      dateNaissance: null,
      departementNaissance: null,
      diplomeOuTitreLePlusEleveObtenu: null,
      dureeMoisPoleEmploi: null,
      email: null,
      nir: null,
      nirTemporaire: null,
      nom: null,
      nomNaissance: null,
      numeroInscriptionPoleEmploi: null,
      prenom: null,
      sexe: null,
      signature: null,
      situationAvantContrat: null,
      telephone: null,
      typeMinimuSocial: null,
      categoriePublic: null,
      contratEngagementJeune: null,
      contratPacea: null,
      inscritFranceTravail: null,
      intituleDiplome: null,
      justifieAncienneteInterim: null,
      niveauEntreeFormation: null,
      nombreHeuresAncienneteInterim: null,
      // Todo: Statuer sur le reset de tuteur
    }
  }

  const patchRemplacerSalarié = store.setupCustomUpdater<SalarieRechercheDTO>((patch, salarieRecherché) => {
    const { natureContrat, niveauFormation, siren, nic, ...salarieRecherchéClean } = salarieRecherché
    patch.salarie = {
      ...créerSalariéVide(),
      ...salarieRecherchéClean,
    }
  })

  // PRT-589 - modifie origine Nir
  const patchSourceSalariéToPortail = store.setupCustomUpdater<{ resetNir: boolean }>((patch, payload) => {
    if (!patch.salarie) return
    patch.salarie.source = 'Portail'
    patch.salarie.signature = null
    if (payload.resetNir) {
      patch.salarie.nir = null
      /* Notifie utilisateur */
      eventBus?.dispatch(NOTIF_SAISIE_SALARIE_MODIFIÉE)
    }
  })

  watch(
    () => ({
      nir: model.value?.salarie?.nir,
      signature: model.value?.salarie?.signature,
    }),
    (newValue, oldValue) => {
      if (model.value?.salarie?.source === 'Portail') return
      if (oldValue.signature !== newValue.signature) return
      if (deepEqual(newValue, oldValue)) return
      patchSourceSalariéToPortail({
        resetNir: false,
      })
    },
  )

  watch(
    () => ({
      nomNaissance: model.value?.salarie?.nomNaissance,
      prenom: model.value?.salarie?.prenom,
      sexe: model.value?.salarie?.sexe,
      dateNaissance: model.value?.salarie?.dateNaissance,
      nirTemporaire: model.value?.salarie?.nirTemporaire,
      signature: model.value?.salarie?.signature,
    }),
    (newValue, oldValue) => {
      if (model.value?.salarie?.source === 'Portail') return
      if (oldValue.signature !== newValue.signature) return
      if (deepEqual(newValue, oldValue)) return
      patchSourceSalariéToPortail({
        resetNir: true,
      })
    },
  )
  function updateDureeMin(value: number | undefined) {
    // Duree minimum mission
    const updateDureeMinInt = store.setupCustomUpdater<number | undefined>((patch, value) => {
      patch.formation!.dureeMinimumMission = value ?? null
    })
    return store.preserveEstModifié(() => updateDureeMinInt(value))
  }

  watch(
    () => {
      // Cas de la valeur par défaut de la dureeMinimumMission du CIPI traité dans le "normalise" du service API
      if (model.value?.sousDispositif === 'CIPI') return ConstantesCIPI.dureeMinMissionEnHeure
      // Cas du CDPI
      const dureeModulesExternes = model.value?.formation?.modules
        ?.filter((m) => m.typeModule === 'Externe')
        ?.reduce((sum, module) => sum + (module.duree ?? 0), 0)

      if (dureeModulesExternes) return Math.ceil(dureeModulesExternes / 3)
    },
    async (newMin, oldMin) => {
      if (readonly.value) return
      if ((newMin ?? null) === (oldMin ?? null)) return
      if ((newMin ?? null) === (model.value?.formation?.dureeMinimumMission ?? null)) return
      await updateDureeMin(newMin)
    },
    { immediate: true },
  )

  return {
    ...actionsDemande,
    alertPlus26AnsLabel,
    patchRemplacerSalarié,
  }
}
